<div class="kyb-result-block" [ngClass]="{ nested: nested }">
  <!-- The collapsed table row already carries this entity's name, type, role, risk and status, so
       the block opens with what the row could not say: anything needing the reviewer's eye. -->
  @if (attentionItems.length) {
  <message-box type="warn">
    <div class="attention-lead">Needs attention</div>
    @for (item of attentionItems; track item) {
    <div>{{ item }}</div>
    }
  </message-box>
  }

  @if (entity.showingPreviousResult) {
  <message-box type="info">
    A newer KYB workflow has not produced a result yet. Showing the most recent available result.
  </message-box>
  }

  @if (!showKybResult) {
  <div class="source-status" aria-label="Entity verification source">
    <span class="source-badge">Manual entry</span>
    <span class="source-badge unverified">Not KYB verified</span>
  </div>
  }

  <div class="top-grid">
    @if (showKybResult || showWorkflowAction()) {
    <div class="details-content">
      <div class="report-actions">
        <div class="section-title">{{ showKybResult ? 'AML results' : 'KYB workflow' }}</div>
        <div class="report-action-buttons">
          @if (showKybResult) {
          <button mat-stroked-button
            [disabled]="!entity.kybReportAvailable"
            [matTooltip]="entity.kybReportAvailable ? 'Download the FrankieOne KYB report' : 'The KYB report is not available yet'"
            (click)="downloadReport.emit(entity)">Download KYB Report</button>
          }
          @if (showWorkflowAction()) {
          <span [matTooltip]="workflowButtonTooltip()">
            <button mat-stroked-button
              [disabled]="workflowStarting || workflowCooldownActive()"
              (click)="runWorkflow.emit(entity)">{{ workflowButtonLabel() }}</button>
          </span>
          }
        </div>
      </div>
      @if (showKybResult) {
      <div class="check-cards">
        @for (check of checkCards; track check.title) {
        <div class="check-card" [ngClass]="checkStateClass(check.passed)">
          <span class="mdi check-icon" [ngClass]="[checkIcon(check.passed), checkStateClass(check.passed)]"></span>
          <div class="check-body">
            <div class="check-heading">
              <span class="check-title">{{ check.title }}</span>
              @if (check.resultToken) {
              <tag-box [state]="check.resultToken"></tag-box>
              }
            </div>
            <span class="check-outcome">{{ check.outcome }}</span>
          </div>
        </div>
        }
      </div>

      @if (entity.issues.length) {
      <div class="issues-list">
        <div class="section-subtitle">Issues ({{ entity.issueCount }})</div>
        @for (issue of entity.issues; track $index) {
        <div class="issue-row">
          <span class="severity" [ngClass]="(issue.severity || '').toLowerCase()">{{ displayValue(issue.severity) }}</span>
          <span class="category">{{ displayValue(issue.category) }}</span>
          <span class="type">{{ displayValue(issue.issue) }}</span>
          @if (issue.description) {
          <span class="description">— {{ issue.description }}</span>
          }
        </div>
        }
      </div>
      }

      @if (entity.aml) {
      <button mat-button class="screening-detail-toggle" (click)="toggleScreeningDetail()">
        <span class="mdi" [ngClass]="screeningDetailExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"></span>
        {{ screeningDetailExpanded ? 'Hide screening detail' : 'Screening detail' }}
      </button>

      @if (screeningDetailExpanded) {
      <div class="fact-grid screening-detail">
        <div class="fact">
          <span class="fact-label">State</span>
          <span class="fact-value">{{ displayValue(entity.aml.state) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">Total hits</span>
          <span class="fact-value">{{ displayValue(entity.aml.totalHits) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">True positives</span>
          <span class="fact-value">{{ displayValue(entity.aml.totalTruePositives) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">False positives</span>
          <span class="fact-value">{{ displayValue(entity.aml.totalFalsePositives) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">Unknown</span>
          <span class="fact-value">{{ displayValue(entity.aml.totalUnknown) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">Unresolved</span>
          <span class="fact-value">{{ displayValue(entity.aml.totalUnresolved) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">Unresolved PEP</span>
          <span class="fact-value">{{ displayValue(entity.aml.unresolvedPep) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">Unresolved sanctions</span>
          <span class="fact-value">{{ displayValue(entity.aml.unresolvedSanction) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">Unresolved watchlist</span>
          <span class="fact-value">{{ displayValue(entity.aml.unresolvedWatchlist) }}</span>
        </div>
        <div class="fact">
          <span class="fact-label">Unresolved adverse media</span>
          <span class="fact-value">{{ displayValue(entity.aml.unresolvedAdverseMedia) }}</span>
        </div>
      </div>
      }
      }
      }
    </div>
    }

    <div class="details-content">
      <div class="section-title">Business Registration Details</div>
      @if (showKybResult) {
      <div class="source-note">Entity ID: {{ displayValue(entity.frankieOneEntityId) }}</div>
      }
      <div class="fact-grid">
        @for (row of visibleRegistryRows; track row.label) {
        <div class="fact">
          <span class="fact-label">{{ row.label }}</span>
          <span class="fact-value">
            @if (row.value === null) {
            <span class="not-available">—</span>
            } @else if (row.kind === 'date') {
            {{ row.value | date:'dd/MM/yyyy' }}
            } @else if (row.kind === 'status') {
            <span class="registry-status" [ngClass]="statusClass(row.positive)">{{ row.value }}</span>
            } @else {
            {{ row.value }}
            }
          </span>
        </div>
        }
      </div>
    </div>
  </div>

  @if (showKybResult) {
  <div class="details-content people-section" [class.editing]="editable">
    @if (hasAcn) {
    <kyb-entity-section [entity]="keyPeopleEntity"
      [forceShowPeople]="true"
      [showEntityName]="false"
      [readonly]="!editable"
      [useLockAffordances]="false"
      [requireDetailsForNewIndividuals]="editable"
      [resolutions]="resolutions"
      [stagedRowStates]="stagedKeyPeople"
      (personChange)="keyPersonChange.emit($event)"
      (personDetailsRequired)="keyPersonDetailsRequired.emit($event)"
      (personResolutionUndo)="personResolutionUndo.emit($event)"></kyb-entity-section>
    } @else {
    <p class="register-note">
      Officeholder, beneficial owner and shareholder registers come from ASIC and are only available for
      entities with an ACN. This entity was verified on its ABN alone.
    </p>
    }

    <!-- The signer/guarantor rules, kept under the people they apply to and updated on every tick.
         Outside the ACN branch above so a rule holding the save back is never off screen. -->
    @if (ruleErrors.length || ruleWarnings.length) {
    <div class="key-people-rules">
      @for (error of ruleErrors; track error) {
      <message-box type="danger">{{ error }}</message-box>
      }
      @for (warning of ruleWarnings; track warning) {
      <message-box type="warn">{{ warning }}</message-box>
      }
    </div>
    }
  </div>
  }

  @if (keyCompanyDetailsForm) {
  <div class="details-content key-company-details" [class.editing]="editable" [formGroup]="keyCompanyDetailsForm">
    <div class="section-title">
      Key Company Details
      @if (editable) {
      <span class="editing-badge">Editing</span>
      }
    </div>
    <p class="register-note">
      These values are printed on the privacy consent form. Changing them re-issues the form to every
      signatory, including anyone who has already signed.
    </p>

    <div class="row">
      <div class="col-lg-4 col-md-12">
        <annual-revenue [formControl]="annualRevenueControl!" [title]="'Annual Revenue'"></annual-revenue>
      </div>
      <div class="col-lg-4 col-md-12">
        <mat-form-field>
          <mat-label>Business Phone</mat-label>
          <input matInput type="text" [formControl]="businessPhoneControl!">
        </mat-form-field>
      </div>
      <div class="col-lg-4 col-md-12">
        <mat-form-field>
          <mat-label>Business Contact Email</mat-label>
          <input matInput type="email" [formControl]="businessContactEmailControl!">
          @if (businessContactEmailControl?.hasError('email')) {
          <mat-error>Enter a valid email address</mat-error>
          }
        </mat-form-field>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <custom-address [formControl]="principalPlaceControl!"
          [title]="'Primary Place of Business'"
          [placesSearchResultFn]="placesSearchResultFn"
          [placesDetailsFn]="placesDetailsFn"></custom-address>
      </div>
    </div>
  </div>
  }

</div>
